
import { Component, Vue } from "vue-property-decorator";

import ListRequest, { Filter, Ordering, OrderType } from "@/models/ListRequest";
import { mapState } from "vuex";
import { TRANSLATIONS_FETCH } from "@/store/ActionTypes";

import vModal from "@/components/vModal.vue";
import AdminUser from "../../models/User/AdminUser";
import CreateAdminUser from "../../models/User/CreateAdminUser";
import UpdateAdminUser from "../../models/User/UpdateAdminUser";
import { UserTypes } from "../../models/User/UserTypes";
import UserRepository from "../../utils/API/UserRepository";
import vTable from "../../components/vTable.vue";

interface UserAdminsState {
  /**
   * Number of packages displayed on the grid
   */
  count: number;
  /**
   * Total entries in the grid
   */
  totalCount: number;
  /**
   * List of admin users in the system determined by page and page size
   */
  adminUsers: AdminUser[];
  /**
   * Current page on the grid
   */
  page: number;
  /**
   * Maximum visible rows in the grid
   */
  pageSize: number;
  /**
   * List of filters
   */
  filters: Filter[];
  /**
   * Ordering
   */
  orders: Ordering;

  /**
   * Model for admin user creation
   */
  createAdminUser: CreateAdminUser;
  /**
   * Model for admin user edit
   */
  updateAdminUser: UpdateAdminUser;
  /**
   * List of coupon ids to be removed
   */
  removeList: number[];

  /**
   * Is the grid loading?
   */
  tableLoading: boolean;
}

@Component({
  components: {
    vModal,
    vTable
  },
  computed: {
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule
    })
  }
})
class AdminUsers extends Vue {
  localizationState: any;

  userAdminsState: UserAdminsState = {
    count: 0,
    totalCount: 0,
    adminUsers: [],
    page: 1,
    pageSize: 25,
    filters: [],
    orders: { field: "", orderType: OrderType.Asc },
    createAdminUser: {
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      password: "",
      confirmPassword: "",
      userType: UserTypes.Administrator
    },
    updateAdminUser: {
      id: 0,
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      userType: UserTypes.Administrator
    },
    removeList: [],

    tableLoading: false
  };

  // Properties for vTable grid
  selectErase = true;
  searchable = true;
  editableTable = true;

  // Modal properties
  showModal = false;
  modalTitle = "";
  showRemovePrompt = false;
  removePromptTitle = "";

  editMode = false;

  /**
   * Method used by the grid after catching the 'changePage'
   * event from vTable
   *
   * @param {any} args
   */
  changePage(args: any) {
    const orderings: Ordering[] = [];
    orderings.push({ ...args[3] });

    this.requestAdminUsers(
      new ListRequest({
        page: args[0],
        pageSize: this.userAdminsState.pageSize,
        filters: args[2],
        orders: orderings
      })
    );
    this.userAdminsState.page = args[0];
    this.userAdminsState.orders = { ...args[3] };
    this.userAdminsState.filters = [...args[2]];
  }

  /**
   * Method used by the grid after catching the 'search'
   * event from vTable
   *
   * @param {Filter[]} filters
   * @param {Ordering} ordering
   */
  search(filters: Filter[], ordering: Ordering) {
    const orderings: Ordering[] = [];
    orderings.push({ ...ordering });

    this.requestAdminUsers(
      new ListRequest({
        page: 1,
        pageSize: this.userAdminsState.pageSize,
        filters: filters,
        orders: orderings
      })
    );

    this.userAdminsState.page = 1;
    this.userAdminsState.orders = { ...ordering };
    this.userAdminsState.filters = [...filters];
  }

  /**
   * Method used by the grid after catching the 'order'
   * event from vTable
   *
   * @param {Filter[]} filters
   * @param {Ordering} ordering
   */
  order(filters: Filter[], ordering: Ordering) {
    const orderings: Ordering[] = [];
    orderings.push({ ...ordering });

    this.requestAdminUsers(
      new ListRequest({
        page: this.userAdminsState.page,
        pageSize: this.userAdminsState.pageSize,
        filters: filters,
        orders: orderings
      })
    );

    this.userAdminsState.orders = { ...ordering };
    this.userAdminsState.filters = [...filters];
  }

  /**
   * Method that resets most variables and closes all modals.
   */
  resetState() {
    this.showModal = false;
    this.editMode = false;
    this.modalTitle = this.translations.get("admins_modal_title_add");

    this.showRemovePrompt = false;
    this.removePromptTitle = "";

    this.userAdminsState.updateAdminUser = {
      id: 0,
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      userType: UserTypes.Administrator
    };

    this.userAdminsState.createAdminUser = {
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      password: "",
      confirmPassword: "",
      userType: UserTypes.Administrator
    };
  }

  /**
   * Method for edit / add user admins modal toggling
   */
  toggleModal() {
    this.showModal = !this.showModal;
  }

  /**
   * Getter for grid headers. Done this way because headers are translatable and async
   */
  get tableHeaders() {
    return [
      {
        name: this.translations.get("admins_table_header_email"),
        key: "Email",
        searchable: true,
        searchString: "",
        sortable: true
      },
      {
        name: this.translations.get("admins_table_header_firstname"),
        key: "FirstName",
        searchable: true,
        searchString: "",
        sortable: true
      },
      {
        name: this.translations.get("admins_table_header_lastname"),
        key: "LastName",
        searchable: true,
        searchString: "",
        sortable: true
      },
      {
        name: this.translations.get("admins_table_header_phone"),
        key: "Phone",
        searchable: true,
        searchString: "",
        sortable: true
      },
      {
        name: this.translations.get("admins_table_header_usertype"),
        key: "UserType",
        searchable: false,
        searchString: "",
        sortable: true
      }
    ];
  }

  /**
   * Setting modal title for Adding new admin user and toggling the modal
   */
  prepareAddMode() {
    this.modalTitle = this.translations.get("admins_modal_title_add");
    this.toggleModal();
  }

  /**
   * Populating form and toggling the modal for editing coupon.
   * Sets the edit mode to true and toggling the modal
   *
   * @param {number} id Id of the package
   */
  prepareEditMode(id: number) {
    this.editMode = true;
    this.modalTitle = this.translations.get("admins_modal_title_edit");

    let found = this.userAdminsState.adminUsers.find((au) => au.id === id);

    if (typeof found !== "undefined") {
      let temp: UpdateAdminUser = {
        id: found.id ? found.id : 0,
        firstName: found.firstName ? found.firstName : "",
        lastName: found.lastName ? found.lastName : "",
        phone: found.phone ? found.phone : "",
        email: found.email ? found.email : "",
        userType: found.userType !== null ? found.userType : UserTypes.Administrator
      };

      this.userAdminsState.updateAdminUser = { ...temp };

      this.toggleModal();
    }
  }

  /**
   * Method for displaying prompt message for selected coupons to be deleted
   *
   * @param {number[]} ids Package ids
   */
  prepareDeletePrompt(ids: number[]) {
    this.userAdminsState.removeList = ids;
    this.removePromptTitle = this.translations.get(
      "admins_delete_prompt_message",
      ids.length
    );
    this.showRemovePrompt = true;
  }

  /**
   * Method for deleting selected coupons and refreshing the grid.
   */
  confirmDelete() {
    // @ts-ignore
    this.$snotify.async(
      this.translations.get(
        "admins_toastr_deleting",
        this.userAdminsState.removeList.length
      ),
      () =>
        new Promise((resolve, reject) => {
          UserRepository.deleteAdminUsers(this.userAdminsState.removeList)
            .then(() => {
              resolve({
                body: this.translations.get(
                  "admins_toastr_deleted",
                  this.userAdminsState.removeList.length
                )
              });

              this.userAdminsState.page = 1;

              this.requestAdminUsers(
                new ListRequest({
                  page: this.userAdminsState.page,
                  pageSize: this.userAdminsState.pageSize,
                  filters: this.userAdminsState.filters,
                  orders: [this.userAdminsState.orders]
                })
              );
            })
            .catch((error) => {
              let message = "";
              if (error.response) {
                message =
                  error.response.data.errorMessages[0].userVisibleMessage;
              }
              else {
                message = this.translations.get("network_error");
              }
              reject({ body: message });
            });
        }),
      {
        timeout: 3000,
        closeOnClick: true
      }
    );
    this.resetState();
  }

  /**
   * Router method for submitting to API
   */
  submit() {
    this.$validator.validateAll('adminUser').then((result) => {
      if (result) {
        if (this.editMode) {
          this.submitUpdate();
        }
        else {
          this.submitNew();
        }
        return;
      }
      this.$snotify.error(this.translations.get("validation-error-message"));
    });
  }

  /**
   * Method for submitting edit to api and refreshing the grid
   */
  submitUpdate() {
    let adminUser = this.userAdminsState.adminUsers.find(
      (adminUser) => adminUser.id === this.userAdminsState.updateAdminUser.id
    );

    if (
      typeof adminUser !== "undefined" &&
      (adminUser.firstName !== this.userAdminsState.updateAdminUser.firstName ||
        adminUser.lastName !== this.userAdminsState.updateAdminUser.lastName ||
        adminUser.phone !== this.userAdminsState.updateAdminUser.phone ||
        adminUser.email !== this.userAdminsState.updateAdminUser.email ||
        adminUser.userType !== this.userAdminsState.updateAdminUser.userType)
    ) {
      // @ts-ignore
      this.$snotify.async(
        this.translations.get("admins_toastr_updating"),
        () =>
          new Promise((resolve, reject) => {
            UserRepository.updateAdminUser(this.userAdminsState.updateAdminUser)
              .then(() => {
                resolve({
                  body: this.translations.get(
                    "admins_toastr_updated",
                    this.userAdminsState.updateAdminUser.firstName
                  )
                });

                this.requestAdminUsers(
                  new ListRequest({
                    page: this.userAdminsState.page,
                    pageSize: this.userAdminsState.pageSize,
                    filters: this.userAdminsState.filters,
                    orders: [this.userAdminsState.orders]
                  })
                );

                this.resetState();
              })
              .catch((error) => {
                let message = "";
                if (error.response) {
                  message =
                    error.response.data.errorMessages[0].userVisibleMessage;
                }
                else {
                  message = this.translations.get("network_error");
                }
                reject({ body: message });
              });
          }),
        {
          timeout: 3000,
          closeOnClick: true
        }
      );
    }
    else {
      this.$snotify.simple(this.translations.get("toastr_nochanges"));
      this.resetState();
    }
  }

  /**
   * Method for submitting new coupon to api and refreshing the grid
   */
  submitNew() {
    // @ts-ignore
    this.$snotify.async(
      this.translations.get("admins_toastr_adding"),
      () =>
        new Promise((resolve, reject) => {
          UserRepository.createAdminUser(this.userAdminsState.createAdminUser)
            .then(() => {
              resolve({
                body: this.translations.get(
                  "admins_toastr_added",
                  this.userAdminsState.createAdminUser.firstName
                )
              });

              this.requestAdminUsers(
                new ListRequest({
                  page: this.userAdminsState.page,
                  pageSize: this.userAdminsState.pageSize,
                  filters: this.userAdminsState.filters,
                  orders: [this.userAdminsState.orders]
                })
              );

              this.resetState();
            })
            .catch((error) => {
              let message = "";
              if (error.response) {
                message =
                  error.response.data.errorMessages[0].userVisibleMessage;
              }
              else {
                message = this.translations.get("network_error");
              }
              reject({ body: message });
            });
        }),
      {
        timeout: 3000,
        closeOnClick: true
      }
    );
  }

  /**
   * Getter method to transform the list of admin users to grid data (vTable)
   */
  get transformAdminUsers() {
    return this.userAdminsState.adminUsers.map(
      (element: AdminUser) => {
        return {
          e1: element.firstName ? element.firstName : "",
          e2: element.lastName ? element.lastName : "",
          e3: element.phone ? element.phone : "",
          e4: this.translations.get(`user_type_${element.userType}`),
          meta: {
            id: element.id,
            link: element.email
          }
        };
      }
    );
  }

  /**
   * Method for fetching admin users based on list request
   *
   * @param {ListRequest} listRequest
   */
  requestAdminUsers(listRequest: ListRequest) {
    this.userAdminsState.tableLoading = true;
    UserRepository.listAdminUsers(listRequest)
      .then((response) => {
        this.userAdminsState.count = response.data.count;
        this.userAdminsState.totalCount = response.data.totalCount;
        this.userAdminsState.adminUsers = response.data.result;

        this.userAdminsState.tableLoading = false;
      })
      .catch((error) => {
        if (error.response)
          this.$snotify.error(
            error.response.errorMessages[0].userVisibleMessage
          );
        else this.$snotify.error(this.translations.get("network-error"));
        this.userAdminsState.tableLoading = false;
      });
  }

  mounted() {
    let request: ListRequest = { ...this.$store.getters.getListRequest };
    this.requestAdminUsers(request);
  }

  created() {
    this.$store.dispatch(TRANSLATIONS_FETCH, {
      category: "pages",
      collections: ["admins", "common"]
    });

    document.title = 'Manage Admin Users | MenuSano';
  }

  get translations() {
    return this.localizationState.translations;
  }
}
export default AdminUsers;
